import * as CryptoJS from "crypto-js";
import { KJUR, KEYUTIL } from "jsrsasign";

/**
 * Utility class for cryptographic operations
 * - Uses CryptoJS for hashing
 * - Uses jsrsasign for asymmetric signing and verification with ECDSA
 */
class Crypto {
    /**
     * Signs a message using the private key
     * @param message - The message to sign
     * @param privateKey - Private key in PEM format
     * @returns An object containing the hash and signature
     */
    static sign(message: string, privateKey: string): { hash: string; signature: string } {
        try {
            // Generate SHA-256 hash of the message (Base64-encoded)
            const hash = CryptoJS.SHA256(message).toString(CryptoJS.enc.Base64);

            // Parse the private key
            const key = KEYUTIL.getKey(privateKey);

            // Sign the hash using ECDSA
            const signer = new KJUR.crypto.Signature({ alg: "SHA256withECDSA" });
            signer.init(key);
            signer.updateString(message);
            const signatureHex = signer.sign();

            // Encode the signature in Base64 using btoa
            const signatureBase64 = Crypto.hexToBase64(signatureHex);

            return { hash, signature: signatureBase64 };
        } catch (error) {
            console.error("Error signing message:", error);
            throw error;
        }
    }

    /**
     * Verifies a signature using the public key
     * @param publicKey - Public key in PEM format
     * @param message - The original message
     * @param signature - The signature to verify (Base64-encoded)
     * @returns Boolean indicating whether the signature is valid
     */
    static verify(publicKey: string, message: string, signature: string): boolean {
        try {
            // Decode the signature from Base64 to hexadecimal using atob
            const signatureHex = Crypto.base64ToHex(signature);

            // Parse the public key
            const key = KEYUTIL.getKey(publicKey);

            // Verify the signature using ECDSA
            const verifier = new KJUR.crypto.Signature({ alg: "SHA256withECDSA" });
            verifier.init(key);
            verifier.updateString(message);
            return verifier.verify(signatureHex);
        } catch (error) {
            console.error("Error verifying signature:", error);
            throw error;
        }
    }

    /**
     * Helper function to convert a hexadecimal string to Base64
     * @param hex - Hexadecimal string
     * @returns Base64-encoded string
     */
    static hexToBase64(hex: string): string {
        const binary = hex
            .match(/.{1,2}/g)!
            .map((byte) => String.fromCharCode(parseInt(byte, 16)))
            .join("");
        return btoa(binary);
    }

    /**
     * Helper function to convert a Base64 string to hexadecimal
     * @param base64 - Base64-encoded string
     * @returns Hexadecimal string
     */
    static base64ToHex(base64: string): string {
        const binary = atob(base64);
        return Array.from(binary)
            .map((char) => char.charCodeAt(0).toString(16).padStart(2, "0"))
            .join("");
    }
}

export default Crypto;